// ─── Validation helpers ───────────────────────────────────────────────────────

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isValidUuid(v: string): boolean {
  return UUID_RE.test(v);
}

export function isNumeric(v: string): boolean {
  return /^\d+$/.test(v);
}

// ─── Date utilities ───────────────────────────────────────────────────────────

/**
 * Converts any recognisable date string to the dd-mm-yyyy format expected by
 * the backend. Supported input formats: dd-mm-yyyy, yyyy-mm-dd, dd/mm/yyyy,
 * mm/dd/yyyy (ambiguous cases default to dd/mm/yyyy since the data is Dutch).
 * Returns null when the input cannot be parsed.
 */
export function toBackendDate(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;

  let day: number, month: number, year: number;

  // dd-mm-yyyy or yyyy-mm-dd (hyphen separator)
  const hyphen = new RegExp(/^(\d{1,4})-(\d{1,2})-(\d{1,4})$/).exec(s);
  if (hyphen) {
    const [, a, b, c] = hyphen;
    if (a.length === 4) {
      // yyyy-mm-dd
      year = Number.parseInt(a, 10);
      month = Number.parseInt(b, 10);
      day = Number.parseInt(c, 10);
    } else {
      // dd-mm-yyyy
      day = Number.parseInt(a, 10);
      month = Number.parseInt(b, 10);
      year = Number.parseInt(c, 10);
    }
  } else {
    // dd/mm/yyyy (Dutch slash format)
    const slash = new RegExp(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/).exec(s);
    if (!slash) return null;
    const [, d, m, y] = slash;
    day = Number.parseInt(d, 10);
    month = Number.parseInt(m, 10);
    year = Number.parseInt(y, 10);
  }

  // Calendar validity check: month 1-12, day valid for that month/year
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return `${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year}`;
}

/**
 * Formats an ISO date string for display using nl-NL locale (dd-mm-yyyy).
 * Returns '—' for null/undefined/invalid values.
 */
export function formatIsoDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Converts a dd-mm-yyyy backend date to a display string (kept as-is).
 * Returns '—' for null/empty values.
 */
export function formatBackendDate(ddmmyyyy: string | null): string {
  if (!ddmmyyyy) return '—';
  const parts = ddmmyyyy.split('-');
  if (parts.length !== 3) return ddmmyyyy;
  return `${parts[0]}-${parts[1]}-${parts[2]}`;
}
